import * as d3 from 'd3';

class PeakDayHeatmapView extends HTMLElement {
    // Save dimensions between renderings
    static dimensions = {
        width: 800,
        height: 600
    };

    connectedCallback() {

        // Listen for window resize to adapt the size
        window.addEventListener('resize', this.handleResize.bind(this));
        
        // Listen for HTMX updates
        document.body.addEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
        
        // Initial rendering
        this.renderPeakDayHeatmap();
    }
    
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        document.body.removeEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
    }
    
    handleResize() {
        if (document.body.contains(this)) {
            const container = this.querySelector('#peak-day-heatmap-container');
            if (container) {
                const rect = container.getBoundingClientRect();
                if (rect.width > 10 && rect.height > 10) {
                    // Update our static dimensions when the size changes
                    PeakDayHeatmapView.dimensions = {
                        width: rect.width,
                        height: rect.height
                    };
                }
            }
            this.renderPeakDayHeatmap();
        }
    }
    
    handleHtmxUpdate() {
        if (document.body.contains(this)) {
            this.renderPeakDayHeatmap();
        }
    }
    
    renderPeakDayHeatmap() {
        const dataEl = this.querySelector('#peak-day-heatmap-data');
        if (!dataEl) return;
        
        try {
            const data = JSON.parse(dataEl.getAttribute('x-data'));
            this.createPeakDayHeatmap(data);
        } catch (error) {
            console.error('Failed to parse peak-day-heatmap data:', error);
        }
    }

    createPeakDayHeatmap(data) {
        const container = this.querySelector('#peak-day-heatmap-container');
        if (!container) return;
        
        // Use container dimensions if available, otherwise the saved ones
        const rect = container.getBoundingClientRect();
        const width = (rect.width > 10) ? rect.width : PeakDayHeatmapView.dimensions.width;
        const height = (rect.height > 10) ? rect.height : PeakDayHeatmapView.dimensions.height;
        
        // If it's a "real" size, save it for future use
        if (width > 10 && height > 10) {
            PeakDayHeatmapView.dimensions = { width, height };
        }
        
        console.log('PeakDayHeatmap dimensions:', width, height);
        const margin = {top: 70, right: 0, bottom: 50, left: 60};
        d3.select(container).selectAll("svg").remove();
        // Extract all dates and hours
        let allDates = new Set();
        let counts = {};
        if (data.children) {
                Object.values(data.children).forEach(arr => {
                        arr.forEach(obj => {
                                const dateObj = typeof obj.year === "string" ? new Date(obj.year) : obj.year;
                                dateObj.setHours(dateObj.getHours() - 1);
                                const date = d3.timeFormat("%Y-%m-%d")(dateObj);
                                const hour = dateObj.getHours();
                                allDates.add(date);
                                const key = `${date}_${hour}`;
                                counts[key] = (counts[key] || 0) + 1;
                        });
                });
        }
        const dates = Array.from(allDates).sort();
        const hours = d3.range(0, 24);

        // Create complete heatmapData with 0 where missing
        let heatmapData = [];
        dates.forEach(date => {
                hours.forEach(hour => {
                        const key = `${date}_${hour}`;
                        heatmapData.push({
                                date,
                                hour,
                                value: counts[key] || 0
                        });
                });
        });

        // Scale and color (discrete color scale)
        const y = d3.scaleBand()
                .domain(dates)
                .range([margin.top, height - margin.bottom])
                .padding(0.05);

        const x = d3.scaleBand()
                .domain(hours)
                .range([margin.left, width - margin.right])
                .padding(0.05);

        // Discrete color scale: egg white → light orange → orange → light red-orange → redder → reddest
        const colorScale = d3.scaleThreshold()
                .domain([1, 2, 3, 4, 5, 6])
                .range([
                        "#fffbea", // 0: egg white
                        "#ffe5b4", // 1: light orange
                        "#ffd580", // 2: orange
                        "#ffb347", // 3: light red-orange
                        "#ff704d", // 4: redder
                        "#ff3300", // 5: even redder
                        "#b30000"  // 6 or more: reddest
                ]);
        // Remove any existing SVG
        d3.select(container).selectAll("svg").remove();
        // Set up SVG dimensions
        const svg = d3.select(container)
                .append("svg")
                .attr("width", width)
                .attr("height", height)
                .attr("viewBox", [0, 0, width, height])
                .attr("style", "max-width: 100%; height: auto;");

        // Draw cells
        svg.selectAll(".cell")
                .data(heatmapData)
                .enter()
                .append("rect")
                .attr("class", "cell")
                .attr("x", d => x(d.hour))
                .attr("y", d => y(d.date))
                .attr("width", x.bandwidth())
                .attr("height", y.bandwidth())
                .attr("fill", d => colorScale(d.value));

        // X-axis (hours)
        svg.append("g")
                .attr("transform", `translate(0,${margin.top})`)
                .call(d3.axisTop(x).tickFormat(d => d.toString().padStart(2, "0") + ":00"))
                .selectAll("text")
                .style("text-anchor", "middle");

        // Y-axis (dates)
        svg.append("g")
                .attr("transform", `translate(${margin.left},0)`)
                .call(d3.axisLeft(y).tickFormat(d => d))
                .selectAll("text")
                .style("font-size", "10px");

        // Y-axis label
        svg.append("text")
                .attr("transform", `rotate(-90)`)
                .attr("x", -(margin.top + (height - margin.top - margin.bottom) / 2))
                .attr("y", 16)
                .attr("text-anchor", "middle")
                .attr("font-size", "14px")
                .attr("fill", "#333")
                .text("Date")
                .style("font-weight", "bold");

        // X-axis label
        svg.append("text")
                .attr("x", width - margin.right)
                .attr("y", margin.top - 40)
                .attr("text-anchor", "end")
                .attr("font-size", "12px")
                .attr("fill", "#333")
                .text("Hour");

        // Title
        svg.append("text")
                .attr("x", width / 2)
                .attr("y", margin.top / 2)
                .attr("text-anchor", "middle")
                .text("Time")
                .style("font-size", "14px")
                .style("font-weight", "bold");

        // Legend (discrete)
        const legendWidth = 280;
        const legendHeight = 14;
        const legendMarginBottom = 24;
        const legendData = [
                { label: "0", color: "#fffbea" },
                { label: "1", color: "#ffe5b4" },
                { label: "2", color: "#ffd580" },
                { label: "3", color: "#ffb347" },
                { label: "4", color: "#ff704d" },
                { label: "5", color: "#ff3300" },
                { label: "6+", color: "#b30000" }
        ];

        const legendGroup = svg.append("g")
                .attr("transform", `translate(${(width - legendWidth) / 2},${height - legendHeight - legendMarginBottom})`);

        legendGroup.selectAll("rect")
                .data(legendData)
                .enter()
                .append("rect")
                .attr("x", (d, i) => i * (legendWidth / legendData.length))
                .attr("width", legendWidth / legendData.length)
                .attr("height", legendHeight)
                .attr("fill", d => d.color);

        legendGroup.selectAll("text")
                .data(legendData)
                .enter()
                .append("text")
                .attr("x", (d, i) => i * (legendWidth / legendData.length) + (legendWidth / legendData.length) / 2)
                .attr("y", legendHeight + 12)
                .attr("text-anchor", "middle")
                .attr("font-size", "10px")
                .attr("fill", "#333")
                .text(d => d.label);

        legendGroup.append("text")
                .attr("x", legendWidth / 2)
                .attr("y", -6)
                .attr("text-anchor", "middle")
                .attr("fill", "#333")
                .attr("font-size", "10px")
                .text("amount of deliveries");


}
}
if (!customElements.get('peak-day-heatmap-view')) {
        customElements.define('peak-day-heatmap-view', PeakDayHeatmapView);
}